/**
 * Browser-side trimming for reference clips. The selected part of a video is
 * played back off screen into a canvas and re-recorded as an H.264 MP4 with the
 * MediaRecorder API (video only). It runs at playback speed, so a 10 s selection
 * takes about 10 s; results are cached per source and range.
 *
 * Every wait has a limit, so a clip that never opens, seeks or plays fails with a
 * reason instead of leaving the node waiting: a file the browser cannot seek in is
 * played forward to the start instead, playback paused by the browser (a tab in the
 * background) is resumed, and a run that can be cancelled stops when it is.
 */

import { CLIP_MAX_BYTES } from './referenceClips';

const MP4_CANDIDATES = ['video/mp4;codecs=avc1.42E01E', 'video/mp4;codecs=avc1', 'video/mp4'];

/** The MP4 recording type this browser supports, or null when it cannot record MP4. */
export const pickRecorderMime = (): string | null => {
    if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') return null;
    return MP4_CANDIDATES.find((candidate) => MediaRecorder.isTypeSupported(candidate)) ?? null;
};

/** How long each step may take before the trim gives up with a reason. */
const OPEN_TIMEOUT_MS = 20_000;
const SEEK_TIMEOUT_MS = 8_000;
const PLAY_TIMEOUT_MS = 10_000;
/** Visible, playing, and the picture has not moved for this long: the clip is stuck. */
const STALL_TIMEOUT_MS = 15_000;
/** Seeking by playing ahead runs this much faster than real time. */
const FAST_FORWARD_RATE = 8;

const abortError = (): DOMException => new DOMException('The clip preparation was cancelled.', 'AbortError');

/**
 * Resolves on `okEvent`; rejects on `failEvent`, after `timeoutMs` (with `timeoutMessage`),
 * or when `signal` aborts.
 */
const waitForEvent = (
    target: EventTarget,
    okEvent: string,
    { failEvent = 'error', timeoutMs, timeoutMessage, signal }: { failEvent?: string; timeoutMs?: number; timeoutMessage?: string; signal?: AbortSignal } = {},
): Promise<void> =>
    new Promise((resolve, reject) => {
        let timer: ReturnType<typeof setTimeout> | undefined;
        const cleanup = () => {
            target.removeEventListener(okEvent, done);
            target.removeEventListener(failEvent, failed);
            signal?.removeEventListener('abort', aborted);
            if (timer) clearTimeout(timer);
        };
        const done = () => { cleanup(); resolve(); };
        const failed = () => { cleanup(); reject(new Error('Could not load the video.')); };
        const aborted = () => { cleanup(); reject(abortError()); };
        if (signal?.aborted) { reject(abortError()); return; }
        target.addEventListener(okEvent, done, { once: true });
        target.addEventListener(failEvent, failed, { once: true });
        signal?.addEventListener('abort', aborted, { once: true });
        if (timeoutMs) timer = setTimeout(() => { cleanup(); reject(new Error(timeoutMessage || 'The video did not respond.')); }, timeoutMs);
    });

/**
 * A muted video element parked off screen. It has to be in the document for
 * frame callbacks to fire while it plays.
 */
const mountVideo = async (url: string, signal?: AbortSignal): Promise<HTMLVideoElement> => {
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    Object.assign(video.style, { position: 'fixed', left: '-9999px', top: '0', width: '2px', height: '2px', opacity: '0', pointerEvents: 'none' });
    video.src = url;
    document.body.appendChild(video);
    try {
        await waitForEvent(video, 'loadedmetadata', {
            timeoutMs: OPEN_TIMEOUT_MS,
            timeoutMessage: 'The clip could not be opened. It may be damaged, or in a format this browser cannot play.',
            signal,
        });
    } catch (e) {
        video.remove();
        throw e;
    }
    return video;
};

/**
 * The video's length, working around files whose header carries no duration
 * (Chrome reports Infinity for WebM recorded by MediaRecorder until a seek past
 * the end reveals the real length).
 */
export const ensureVideoDuration = async (video: HTMLVideoElement): Promise<number> => {
    if (Number.isFinite(video.duration) && video.duration > 0) return video.duration;
    const revealed = new Promise<void>((resolve) => {
        const done = () => { video.removeEventListener('durationchange', done); video.removeEventListener('seeked', done); resolve(); };
        video.addEventListener('durationchange', done);
        video.addEventListener('seeked', done);
        setTimeout(done, 3000);
    });
    video.currentTime = 1e101;
    await revealed;
    const duration = Number.isFinite(video.duration) ? video.duration : 0;
    video.currentTime = 0;
    return duration;
};

/** Length of a video file in seconds (0 when the browser cannot tell). */
export const probeVideoDuration = async (source: Blob, signal?: AbortSignal): Promise<number> => {
    const url = URL.createObjectURL(source);
    let video: HTMLVideoElement | null = null;
    try {
        video = await mountVideo(url, signal);
        return await ensureVideoDuration(video);
    } finally {
        video?.remove();
        URL.revokeObjectURL(url);
    }
};

/** Plays muted video, giving up after PLAY_TIMEOUT_MS. */
const startPlaying = async (video: HTMLVideoElement): Promise<void> => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
        await Promise.race([
            video.play(),
            new Promise<never>((_, reject) => {
                timer = setTimeout(() => reject(new Error('The clip would not start playing, so it could not be trimmed.')), PLAY_TIMEOUT_MS);
            }),
        ]);
    } finally {
        if (timer) clearTimeout(timer);
    }
};

/**
 * Moves to `at`: a normal seek first; when the file does not answer it (some files carry
 * no index to seek with), plays ahead fast until it gets there.
 */
const goTo = async (video: HTMLVideoElement, at: number, signal?: AbortSignal): Promise<void> => {
    if (at <= 0.05 && video.currentTime <= 0.05) return; // already at the start
    try {
        const seeked = waitForEvent(video, 'seeked', { timeoutMs: SEEK_TIMEOUT_MS, signal });
        video.currentTime = at;
        await seeked;
        if (Math.abs(video.currentTime - at) < 0.5) return;
    } catch (e) {
        if ((e as { name?: string })?.name === 'AbortError') throw e;
    }
    // Fast-forward from wherever it is
    video.playbackRate = FAST_FORWARD_RATE;
    try {
        await startPlaying(video);
        const reached = new Promise<void>((resolve, reject) => {
            let lastTime = video.currentTime;
            let lastMove = performance.now();
            const check = setInterval(() => {
                if (signal?.aborted) { clearInterval(check); reject(abortError()); return; }
                if (video.currentTime >= at - 0.05 || video.ended) { clearInterval(check); resolve(); return; }
                if (video.currentTime !== lastTime) { lastTime = video.currentTime; lastMove = performance.now(); }
                else if (!document.hidden && performance.now() - lastMove > STALL_TIMEOUT_MS) {
                    clearInterval(check);
                    reject(new Error('The browser could not move to the start of the selected part of the clip.'));
                }
            }, 50);
        });
        await reached;
    } finally {
        video.pause();
        video.playbackRate = 1;
    }
};

const bitrateFor = (width: number, height: number): number => {
    const pixels = width * height;
    if (pixels >= 1920 * 1080 * 0.9) return 8_000_000;
    if (pixels >= 1280 * 720 * 0.9) return 5_000_000;
    return 3_000_000;
};

export interface TrimmedClip {
    blob: Blob;
    mimeType: string;
    durationSec: number;
}

/** Where a trim is: opening the file, moving to the start, recording, or held up by a tab in the background. */
export type TrimStep = 'opening' | 'seeking' | 'trimming' | 'waiting';

export interface TrimProgress {
    step: TrimStep;
    /** How much of the selection is recorded, 0..1. */
    fraction: number;
}

/**
 * Re-record the part of `source` between `startSec` and `endSec` as an MP4.
 * Frames are capped at 1920 px on the long side; audio is not carried over.
 */
export const trimVideoToMp4 = async (
    source: Blob,
    startSec: number,
    endSec: number,
    onProgress?: (progress: TrimProgress) => void,
    signal?: AbortSignal,
    /** At most this bitrate, so a long clip still fits in the request (see clipBitrateBudget). */
    maxBitsPerSecond?: number,
): Promise<TrimmedClip> => {
    const recorderMime = pickRecorderMime();
    if (!recorderMime) throw new Error('This browser cannot produce MP4 clips. Use a current Chrome or Edge.');
    const mimeType = recorderMime.split(';')[0];
    if (signal?.aborted) throw abortError();

    const url = URL.createObjectURL(source);
    let video: HTMLVideoElement | null = null;
    let watchdog: ReturnType<typeof setInterval> | undefined;
    let onVisible: (() => void) | undefined;
    try {
        onProgress?.({ step: 'opening', fraction: 0 });
        video = await mountVideo(url, signal);
        const total = await ensureVideoDuration(video);
        const start = Math.min(Math.max(0, startSec), total || startSec);
        const end = total > 0 ? Math.min(Math.max(start, endSec), total) : Math.max(start, endSec);
        if (end - start <= 0) throw new Error('The selected part of the clip is empty.');

        onProgress?.({ step: 'seeking', fraction: 0 });
        await goTo(video, start, signal);

        const sourceWidth = video.videoWidth || 1280;
        const sourceHeight = video.videoHeight || 720;
        const scale = Math.min(1, 1920 / Math.max(sourceWidth, sourceHeight));
        const even = (value: number) => Math.max(2, Math.round((value * scale) / 2) * 2); // H.264 wants even dimensions
        const canvas = document.createElement('canvas');
        canvas.width = even(sourceWidth);
        canvas.height = even(sourceHeight);
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Canvas is not available.');

        const stream = canvas.captureStream(0);
        const track = stream.getVideoTracks()[0] as CanvasCaptureMediaStreamTrack;
        const recorder = new MediaRecorder(stream, { mimeType: recorderMime, videoBitsPerSecond: Math.min(bitrateFor(canvas.width, canvas.height), maxBitsPerSecond ?? Infinity) });
        const chunks: BlobPart[] = [];
        recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunks.push(e.data); };

        const playing = video;
        let stopped = false;
        let failure: Error | null = null;
        const finished = new Promise<void>((resolve, reject) => {
            recorder.onstop = () => (failure ? reject(failure) : resolve());
            recorder.onerror = (e) => reject((e as { error?: Error }).error ?? new Error('Recording failed.'));
        });
        const stop = (error?: Error) => {
            if (stopped) return;
            stopped = true;
            if (error) failure = error;
            playing.pause();
            if (recorder.state !== 'inactive') recorder.stop();
        };
        const onAbort = () => stop(abortError() as unknown as Error);
        signal?.addEventListener('abort', onAbort, { once: true });

        ctx.drawImage(playing, 0, 0, canvas.width, canvas.height);

        // Frames are painted on each new video frame; when those callbacks stop arriving while
        // the picture still moves (it happens with hidden video), animation frames take over
        let lastPaint = performance.now();
        let useAnimationFrames = false;
        const paint = () => {
            if (stopped) return;
            lastPaint = performance.now();
            ctx.drawImage(playing, 0, 0, canvas.width, canvas.height);
            track.requestFrame();
            onProgress?.({ step: 'trimming', fraction: Math.min(1, (playing.currentTime - start) / (end - start)) });
            if (playing.currentTime >= end - 0.02 || playing.ended) {
                stop();
                return;
            }
            schedule();
        };
        const schedule = () => {
            const withCallback = playing as HTMLVideoElement & { requestVideoFrameCallback?: (cb: () => void) => number };
            if (!useAnimationFrames && typeof withCallback.requestVideoFrameCallback === 'function') withCallback.requestVideoFrameCallback(paint);
            else requestAnimationFrame(paint);
        };
        playing.addEventListener('ended', () => stop(), { once: true });

        // A tab in the background pauses the video; play on once it is in front again
        onVisible = () => {
            if (!document.hidden && !stopped && playing.paused) void playing.play().catch(() => {});
        };
        document.addEventListener('visibilitychange', onVisible);

        // Watch for a picture that no longer moves
        let lastTime = playing.currentTime;
        let lastMove = performance.now();
        watchdog = setInterval(() => {
            if (stopped) return;
            const now = performance.now();
            if (document.hidden) {
                lastMove = now; // not stuck: waiting for the tab
                onProgress?.({ step: 'waiting', fraction: Math.min(1, Math.max(0, (playing.currentTime - start) / (end - start))) });
                return;
            }
            if (playing.currentTime !== lastTime) {
                lastTime = playing.currentTime;
                lastMove = now;
                if (now - lastPaint > 1000 && !useAnimationFrames) {
                    useAnimationFrames = true;
                    schedule();
                }
            } else if (playing.paused && !playing.ended) {
                void playing.play().catch(() => {});
            }
            if (now - lastMove > STALL_TIMEOUT_MS) {
                stop(new Error('The clip stopped playing while it was being trimmed. Try again; if it keeps happening, trim a different part of the clip.'));
            }
        }, 500);

        // Where recording really starts: playing forward to the start can overshoot it a little
        const recordedFrom = Math.max(start, playing.currentTime);
        recorder.start(250);
        track.requestFrame();
        try {
            await startPlaying(playing);
        } catch (e) {
            stop(e instanceof Error ? e : new Error(String(e)));
        }
        if (!stopped) schedule();
        try {
            await finished;
        } finally {
            signal?.removeEventListener('abort', onAbort);
        }

        const blob = new Blob(chunks, { type: mimeType });
        if (blob.size === 0) throw new Error('The clip came out empty.');
        if (blob.size > CLIP_MAX_BYTES) {
            throw new Error(`The clip is ${(blob.size / 1024 / 1024).toFixed(0)} MB; Venice takes at most 50 MB. Choose a shorter part.`);
        }
        onProgress?.({ step: 'trimming', fraction: 1 });
        return { blob, mimeType, durationSec: Math.max(0.1, end - recordedFrom) };
    } finally {
        if (watchdog) clearInterval(watchdog);
        if (onVisible) document.removeEventListener('visibilitychange', onVisible);
        video?.remove();
        URL.revokeObjectURL(url);
    }
};

const MAX_CACHED = 6;

interface CachedTrim {
    task: Promise<TrimmedClip>;
    controller: AbortController;
    listeners: Set<(progress: TrimProgress) => void>;
    last?: TrimProgress;
    /** Callers waiting on it; when the last one cancels, the trim stops. */
    waiting: number;
}
const cache = new Map<string, CachedTrim>();

/**
 * Trim once per source and range; a retry of the same generation reuses the clip, and one
 * that starts while the first is still running follows its progress. A trim that fails or is
 * cancelled by everyone waiting on it is forgotten, so the next attempt starts afresh.
 */
export const getTrimmedClip = (
    cacheKey: string,
    loadSource: () => Promise<Blob>,
    startSec: number,
    endSec: number,
    onProgress?: (progress: TrimProgress) => void,
    signal?: AbortSignal,
    maxBitsPerSecond?: number,
): Promise<TrimmedClip> => {
    if (signal?.aborted) return Promise.reject(abortError());
    const key = `${cacheKey}|${startSec.toFixed(2)}|${endSec.toFixed(2)}|${maxBitsPerSecond ? Math.round(maxBitsPerSecond / 100_000) : 'full'}`;
    let entry = cache.get(key);
    if (!entry) {
        const controller = new AbortController();
        const listeners = new Set<(progress: TrimProgress) => void>();
        const created: CachedTrim = { task: Promise.resolve() as unknown as Promise<TrimmedClip>, controller, listeners, waiting: 0 };
        const report = (progress: TrimProgress) => {
            created.last = progress;
            listeners.forEach(listener => listener(progress));
        };
        created.task = loadSource().then((blob) => {
            if (controller.signal.aborted) throw abortError();
            return trimVideoToMp4(blob, startSec, endSec, report, controller.signal, maxBitsPerSecond);
        });
        created.task.catch(() => { if (cache.get(key) === created) cache.delete(key); });
        cache.set(key, created);
        if (cache.size > MAX_CACHED) {
            const oldest = cache.keys().next().value;
            if (oldest !== undefined && oldest !== key) cache.delete(oldest);
        }
        entry = created;
    }
    const current = entry;
    if (onProgress) {
        current.listeners.add(onProgress);
        if (current.last) onProgress(current.last);
    }
    current.waiting += 1;
    return new Promise<TrimmedClip>((resolve, reject) => {
        const release = () => {
            if (onProgress) current.listeners.delete(onProgress);
            signal?.removeEventListener('abort', cancel);
        };
        const cancel = () => {
            release();
            current.waiting -= 1;
            if (current.waiting <= 0) {
                current.controller.abort();
                if (cache.get(key) === current) cache.delete(key);
            }
            reject(abortError());
        };
        signal?.addEventListener('abort', cancel, { once: true });
        current.task.then(
            (clip) => { release(); current.waiting -= 1; resolve(clip); },
            (error) => { release(); current.waiting -= 1; reject(error); },
        );
    });
};
